/**
 * AssetTextComposer — write / paste context directly, no file required.
 *
 * The sibling of <AssetUploadDropzone />. Instead of dragging a file, the
 * user types or pastes prose (the 80% case for RULES: "our tone is X",
 * "always cite sources", …). On Save we wrap the text in a ``.txt`` File and
 * hand the parent the SAME ``onPicked(prepared)`` payload the dropzone uses —
 * so the rest of the pipeline (ConfigureAssetDialog → presign → PUT →
 * createAsset, dedupe-by-hash, RULES/REFERENCE typing, mounts) is identical.
 * The author dropzone already documents this: the Configure step "is the same
 * whether the user uploaded a file or pasted text."
 *
 * "✨ Improve with AI" is opt-in and never auto-rewrites: it calls the
 * stateless /tools/refine endpoint and shows the suggestion as accept/reject
 * (the model PROPOSES, the user DISPOSES — same rule as the skill refiner).
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Sparkles, Check, X } from 'lucide-react';
import { computeFileSha256 } from '../../utils/contentHash';
import { refineText } from '../../services/dataPlaneApi';

const DEFAULT_MAX_CHARS = 48 * 1024; // mirrors backend _REFINE_TEXT_MAX_CHARS / RULES budget headroom

/** Turn a free-form title into a safe, lowercase ``.txt`` filename. */
function slugToTxtFilename(title) {
  const base = String(title || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${base || 'context'}.txt`;
}

/**
 * @param {{
 *   onPicked: (prepared: Array<{
 *     file: File, fileName: string, contentType: string,
 *     size: number, contentHash: string,
 *   }>) => void,
 *   intent?: 'rules'|'reference'|null,   // hint for the AI refiner
 *   maxChars?: number,
 *   disabled?: boolean,
 *   className?: string,
 * }} props
 */
export default function AssetTextComposer({
  onPicked,
  intent = null,
  maxChars = DEFAULT_MAX_CHARS,
  disabled = false,
  className = '',
}) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Refiner state: the proposed suggestion (accept/reject), null when none.
  const [suggestion, setSuggestion] = useState(null); // { text, why }
  const [refining, setRefining] = useState(false);

  const charCount = text.length;
  const overLimit = charCount > maxChars;
  const canSave = !disabled && !busy && text.trim().length > 0 && !overLimit;

  const fileName = useMemo(() => slugToTxtFilename(title), [title]);

  const onImprove = useCallback(async () => {
    const draft = text.trim();
    if (!draft || refining) return;
    setRefining(true);
    setError(null);
    setSuggestion(null);
    try {
      const resp = await refineText({ text: draft, intent });
      if (resp?.improved && resp?.suggestion?.text) {
        setSuggestion({
          text: String(resp.suggestion.text),
          why: String(resp.suggestion.why || ''),
        });
      } else {
        setError(resp?.message || 'No improvement suggested — keep your version.');
      }
    } catch (e) {
      setError(e?.message || 'Improve with AI is unavailable right now.');
    } finally {
      setRefining(false);
    }
  }, [text, intent, refining]);

  const acceptSuggestion = useCallback(() => {
    if (suggestion?.text) setText(suggestion.text);
    setSuggestion(null);
  }, [suggestion]);

  const rejectSuggestion = useCallback(() => setSuggestion(null), []);

  const onSave = useCallback(async () => {
    const body = text.replace(/\s+$/g, '') + '\n'; // trailing newline, like a real file
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const file = new File([body], fileName, { type: 'text/plain' });
      const contentHash = await computeFileSha256(file);
      onPicked?.([
        {
          file,
          fileName,
          contentType: 'text/plain',
          size: file.size,
          contentHash,
        },
      ]);
      // Clear so the composer is ready for the next entry once the parent's
      // Configure dialog has taken over.
      setTitle('');
      setText('');
      setSuggestion(null);
    } catch (e) {
      setError(e?.message || 'Could not prepare the text.');
    } finally {
      setBusy(false);
    }
  }, [text, fileName, onPicked]);

  return (
    <div
      data-testid="data-plane-text-composer"
      className={`flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 ${className}`.trim()}
    >
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={disabled || busy}
          placeholder="Title (becomes the file name)"
          maxLength={80}
          className="flex-1 rounded-md border border-gray-300 px-2.5 py-1 text-sm focus:border-blue-400 focus:outline-none"
          data-testid="dp-text-title"
        />
        <span className="shrink-0 text-[11px] text-gray-400">{fileName}</span>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={disabled || busy}
        rows={8}
        placeholder="Write or paste your context here. RULES are always-injected standing guidance; REFERENCE is retrieved on demand."
        className="w-full resize-y rounded-md border border-gray-300 px-2.5 py-2 text-sm leading-relaxed focus:border-blue-400 focus:outline-none"
        data-testid="dp-text-body"
      />

      {/* AI suggestion — accept/reject, never auto-applied. */}
      {suggestion ? (
        <div
          className="rounded-md border border-violet-200 bg-violet-50 p-2.5"
          data-testid="dp-text-suggestion"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700">
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Suggested improvement
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={acceptSuggestion}
                className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-violet-700"
                data-testid="dp-text-suggestion-accept"
              >
                <Check className="h-3 w-3" aria-hidden /> Use this
              </button>
              <button
                type="button"
                onClick={rejectSuggestion}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
                data-testid="dp-text-suggestion-reject"
              >
                <X className="h-3 w-3" aria-hidden /> Keep mine
              </button>
            </div>
          </div>
          {suggestion.why ? (
            <div className="mb-1 text-[11px] italic text-violet-600">{suggestion.why}</div>
          ) : null}
          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words text-[12px] text-gray-800">
            {suggestion.text}
          </pre>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onImprove}
            disabled={disabled || busy || refining || !text.trim()}
            className="inline-flex items-center gap-1 rounded-md border border-violet-300 px-2.5 py-1 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-50 disabled:opacity-50"
            data-testid="dp-text-improve"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {refining ? 'Improving…' : '✨ Improve with AI'}
          </button>
          <span
            className={`text-[11px] ${overLimit ? 'text-red-600' : 'text-gray-400'}`}
          >
            {charCount.toLocaleString()} / {maxChars.toLocaleString()} chars
          </span>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          data-testid="dp-text-save"
        >
          {busy ? 'Preparing…' : 'Save as context'}
        </button>
      </div>

      {error ? (
        <div className="text-[11px] text-red-600" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
